/* eslint-disable clubmed/no-fake-component */
import moment from 'moment';
import React from 'react';
import { Card } from 'Components/frameworks/card/Card';
import { COLUMN_2 } from 'Components/frameworks/card/Card.constants';
import { formatWeekdayLong } from 'Components/frameworks/datepicker/Datepicker.utils';
import { Icon } from 'Components/frameworks/icon/Icon';
import Image from 'Components/frameworks/image/Image';
import type { TabsItem } from 'Components/frameworks/tabs/Tabs';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import { API_DATE_FORMAT, UTC_DATE_TIME_FORMAT } from 'Utils/date/apiDate';
import { buildProductArgs } from 'Utils/graphql';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { LINE_QRCODE_PRODUCT_PROGRAM } from 'Utils/request/requestDictionary';
export type Activity = {
  endDateTime: string;
  image: string;
  label: string;
  locationLabel: string;
  startDateTime: string;
  weekdayName: string;
};
type ResortPlanning = Array<Activity>;
type GetTabsItemsProps = {
  associatedActivities: Array<ResortPlanning>;
  displayedDays: Array<moment$Moment>;
  language: string;
  programMissingText: string;
  programMissingTitle: string;
};

// Implementation carried on the component side to correct the problem of the timezone of the resort
const getActivitiesQuery = (productArgs, firstDate, lastDate) => `
query {
    resort(${productArgs}) {
      planning(type: "ACTIVITY", firstDate: "${firstDate}", lastDate: "${lastDate}") {
        endDateTime
        image
        label
        locationLabel
        startDateTime
        weekdayName
      }
    }
  }`;

export const getPlanningActivities = (resortId: string, appContext: AppContext) => {
  const productArgs = buildProductArgs(resortId);
  const firstDate = moment().format(API_DATE_FORMAT);
  const lastDate = moment(firstDate, API_DATE_FORMAT).add(7, 'days').format(API_DATE_FORMAT);
  const response = graphQLClient.runQuery(
    getActivitiesQuery(productArgs, firstDate || '', lastDate || ''),
    appContext,
    {
      requestName: LINE_QRCODE_PRODUCT_PROGRAM,
    },
  );
  return response;
};
export const getTabsItems = ({
  associatedActivities,
  displayedDays,
  language,
  programMissingText,
  programMissingTitle,
}: GetTabsItemsProps): Array<TabsItem> =>
  displayedDays.map((day, index) => {
    const dayName =
      associatedActivities[index].length > 0
        ? associatedActivities[index][0].weekdayName
        : formatWeekdayLong(day.day(), language);
    const content =
      associatedActivities[index].length > 0
        ? associatedActivities[index].map(
            ({ endDateTime, image, label, locationLabel, startDateTime }) => {
              const momentStartTime = moment(startDateTime, UTC_DATE_TIME_FORMAT);
              const momentEndTime = moment(endDateTime, UTC_DATE_TIME_FORMAT);
              const duration = momentEndTime.diff(momentStartTime, 'minutes');
              return (
                <div key={`programDay${index}`} className="flex flex-col">
                  <div className="flex min-h-32 items-center rounded-4 border-s-1 border-sienna font-brand text-16 font-normal leading-tight text-black md:text-18">
                    <span className="whitespace-nowrap pl-10 pr-10">{`${momentStartTime.format('HH:mm')} - ${momentEndTime.format('HH:mm')}`}</span>
                    <hr className="mx-0 my-10 block h-1 w-full border-0 border-t-1 border-solid border-mediumGrey p-0 md:my-15 lg:my-20" />
                  </div>
                  <div className="mb-30 mt-10 flex h-full overflow-hidden rounded-12 border-1 border-lightGrey shadow-diffuse">
                    <Image
                      className="me-10 w-1/2 md:me-15 lg:me-20"
                      classNameImg="overflow-hidden"
                      imageUrl={image}
                      key={image}
                      groupKey="program-images"
                      ratioKey={ratioUtils.XL}
                      ratioDisplay={1 / 3}
                    />
                    <div className="mb-10 mt-10 flex min-h-full flex-1 flex-col justify-between md:mb-15 md:mt-15 lg:mb-20 lg:mt-20">
                      <span className="text-16 font-bold leading-tight text-black">{label}</span>
                      <div className="flex h-full flex-col justify-end">
                        <div className="flex pe-10 pt-10 text-14 font-normal leading-tight text-black md:pe-15 md:pt-15 md:text-16 lg:pe-20 lg:pt-20">
                          <Icon
                            className="transition-transform me-5 text-sienna duration-250 ease-in"
                            name={ICONS.ADDRESS}
                            width="1rem"
                          />
                          <span>{locationLabel}</span>
                        </div>
                        <div className="flex pt-10 text-14 font-normal leading-tight text-black md:text-16">
                          <Icon
                            className="transition-transform me-5 text-sienna duration-250 ease-in"
                            name={ICONS.DURATION}
                            width="1rem"
                          />
                          <span>{`${duration} min`}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            },
          )
        : [
            <Card
              color={COLORS.SIENNA}
              heading={{
                iconName: ICONS.ACTIVITIES,
                title: programMissingTitle,
              }}
              key={`programDay${index}`}
              layout={COLUMN_2}
              text={{
                text: programMissingText,
              }}
            />,
          ];
    return {
      header: {
        title: (
          <div className="whitespace-nowrap capitalize" title={dayName}>
            {`${dayName} ${day.format('DD')}`}
          </div>
        ),
      },
      content,
      headerId: `resortPlanningProgramDay${index}Header`,
      panelId: `resortPlanningProgramDay${index}Panel`,
    };
  });
